import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from 'flow/hooks/useRedux';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import {
    selectCallRecordQueueKey,
    selectExtendedTranscriptionIsPending,
    selectExtendedTranscriptionIsPersonal,
    selectExtendedTranscriptionKey,
} from 'flow/selectors/call-records-selectors';
import { useGetExtendedTranscription } from './useGetExtendedTranscription';
import { usePollCallRecordWhilePending } from './usePollCallRecordWhilePending';

/**
 * The extended transcription of a call record, together with the polling and cache handling it
 * needs. Shared by the call record detail page and the ticket call card so both behave identically —
 * previously each wired the poll and the query up itself, and only one of them invalidated the
 * cache, so a regenerated transcription could render stale on the other.
 */
export const useCallRecordExtendedTranscription = (
    callRecordKey: CallRecordKey,
) => {
    const queryClient = useQueryClient();

    const extendedTranscriptionKey = useAppSelector((state: State) =>
        selectExtendedTranscriptionKey(state, callRecordKey),
    );
    const transcriptionIsPending = useAppSelector((state: State) =>
        selectExtendedTranscriptionIsPending(state, callRecordKey),
    );
    const transcriptionIsPersonal = useAppSelector((state: State) =>
        selectExtendedTranscriptionIsPersonal(state, callRecordKey),
    );
    const callRecordQueueKey = useAppSelector((state: State) =>
        selectCallRecordQueueKey(state, callRecordKey),
    );

    usePollCallRecordWhilePending(
        callRecordKey,
        transcriptionIsPending,
        callRecordQueueKey,
    );

    const { data: extendedTranscription } = useGetExtendedTranscription(
        extendedTranscriptionKey,
        transcriptionIsPending,
        transcriptionIsPersonal,
    );

    // The extendedTranscriptionKey is reused across regenerations (e.g. retry after
    // QUOTA_EXCEEDED), so the cached DTO can correspond to a previous run. When the
    // pending flag flips back from true to false, force a refetch so we don't render
    // the previous run's result.
    const previousPendingRef = useRef(transcriptionIsPending);
    useEffect(() => {
        const wasPending = previousPendingRef.current;
        previousPendingRef.current = transcriptionIsPending;
        if (wasPending && !transcriptionIsPending && extendedTranscriptionKey) {
            void queryClient.invalidateQueries({
                queryKey: ['extendedTranscription', extendedTranscriptionKey],
            });
        }
    }, [transcriptionIsPending, extendedTranscriptionKey, queryClient]);

    return extendedTranscription;
};
